import { useState, useEffect, useCallback, useMemo } from 'react';
import { useCadrageStore } from '../stores/cadrageStore'; // Utiliser le store Cadrage
import useCaseService from '../services/useCaseService';
import interviewService from '../services/interviewService';
import cadrageService from '../services/cadrageService';
import i18n from '../i18n/config';
import { Interview, NewInterviewData, InterviewUpdate, InterviewCreate } from '../types';

// Interface pour la valeur de retour du hook
interface UseCadrageGuidedInterviewsReturn {
  interviews: Interview[];
  isLoading: boolean;
  error: string | null;
  selectedInterviewId: string | null; // ID de l'entretien sélectionné (pour affichage simple)
  selectInterview: (id: string | null) => void; // Action pour sélectionner un entretien
  selectedInterviewData: Interview | undefined; // Données complètes de l'entretien sélectionné
  updateInterview: (interviewId: string, payload: InterviewUpdate) => Promise<void>; // Pour sauvegarder depuis la modale
  createGuidedInterview: (data: Partial<NewInterviewData>) => Promise<Interview>; // Pour créer depuis la modale
  interviewToEdit: Interview | null; // Entretien actuellement en cours d'édition dans la modale
  selectInterviewToEdit: (interview: Interview | null) => void; // Pour définir quel entretien éditer
  isEditModalOpen: boolean; // État d'ouverture de la modale d'édition
  openEditModal: () => void; // Pour ouvrir la modale d'édition
  closeEditModal: () => void; // Pour fermer la modale d'édition
  deleteInterview: (interviewId: string) => Promise<void>; // Pour supprimer un entretien
}

/**
 * Hook pour gérer les entretiens guidés associés à un Cadrage.
 * Combine la logique de récupération, CRUD et gestion de la modale d'édition.
 * @param cadrageId - L'ID du cadrage concerné.
 */
export function useCadrageGuidedInterviews(cadrageId: string | undefined): UseCadrageGuidedInterviewsReturn {
  // --- Store State & Actions ---
  // Récupérer les entretiens depuis le store Cadrage (supposés chargés par loadCadrageData)
  const storeInterviews = useCadrageStore((state) => state.interviews);
  const selectedInterviewId = useCadrageStore((state) => state.selectedInterviewId);
  const selectInterview = useCadrageStore((state) => state.selectInterview);
  // Utiliser loadCadrageData pour rafraîchir toutes les données après CRUD
  const loadCadrageData = useCadrageStore((state) => state.loadCadrageData);
  const isLoading = useCadrageStore((state) => state.isLoading); // État de chargement global
  const error = useCadrageStore((state) => state.error); // État d'erreur global

  // --- Local State for Edit Modal ---
  const [interviewToEdit, setInterviewToEdit] = useState<Interview | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false); // État de soumission pour CRUD

  // --- Data Fetching ---
  // Le chargement initial est géré par useCadrageData qui appelle loadCadrageData.
  // On s'assure juste que les données sont bien récupérées via le store.
  useEffect(() => {
    if (cadrageId) {
      // Optionnel: déclencher loadCadrageData ici si ce hook doit être autonome
      // console.log(`useCadrageGuidedInterviews: Ensuring data is loaded for cadrage ${cadrageId}`);
      // loadCadrageData(cadrageId); // Décommenter si nécessaire
    }
  }, [cadrageId]); // Pas besoin de loadCadrageData comme dépendance ici

  // --- Derived Data ---
  // Données complètes de l'entretien sélectionné (pour affichage simple ou autre usage)
  const selectedInterviewData = useMemo(() => {
    return storeInterviews.find((i: Interview) => i.id === selectedInterviewId);
  }, [storeInterviews, selectedInterviewId]);

  // --- CRUD Actions ---

  // CREATE
  const createGuidedInterview = useCallback(async (data: Partial<NewInterviewData>): Promise<Interview> => {
    if (!cadrageId) throw new Error(i18n.t('errors.interview.missingCadrageForCreate'));
    if (!data.templateId) throw new Error(i18n.t('errors.interview.templateRequired'));

    // Get domain_id from the cadrage
    const cadrageData = useCadrageStore.getState().cadrageData;
    if (!cadrageData?.use_case_id) {
      throw new Error(i18n.t('errors.interview.useCaseNotFoundInCadrage'));
    }

    // Fetch the use case to get domain_id
    const useCaseData = await useCaseService.get(cadrageData.use_case_id);
    const domain_id = useCaseData.domain_id;

    if (!domain_id) {
      throw new Error(i18n.t('errors.interview.domainNotFoundInUseCase'));
    }

    // The default name is persisted server-side as the interview's name, so it stays a fixed literal.
    const payload: InterviewCreate = {
      template_id: data.templateId,
      domain_id: domain_id,
      cadrage_id: cadrageId,
      name: data.title || `Nouvel Entretien Cadrage (Template: ${data.templateId.substring(0, 6)})`,
      status: 'Planned',
    };

    setIsSubmitting(true);
    try {
      console.log(`useCadrageGuidedInterviews: Création d'un entretien pour le cadrage ${cadrageId}`, payload);
      const interview = await interviewService.createInterview(payload);

      // --- Modification : Rafraîchir uniquement la liste des entretiens ---
      try {
        const interviews = await cadrageService.getInterviews(cadrageId);
        // Mettre à jour directement le store avec la nouvelle liste
        useCadrageStore.setState({ interviews: interviews || [] });
        console.log("Liste des entretiens mise à jour dans le store après création.");
      } catch (refreshError) {
        console.error("Erreur lors du rafraîchissement de la liste des entretiens après création:", refreshError);
        // Propager l'erreur pour qu'elle soit gérée par le catch externe
        throw refreshError;
      }
      // --- Fin Modification ---

      // Sélectionner et retourner l'entretien créé APRÈS la tentative de rafraîchissement
      selectInterview(interview.id);
      return interview;
    } catch (err: any) { // Ce catch gérera maintenant aussi l'erreur de rafraîchissement
      console.error("Échec de la création ou du rafraîchissement de l'entretien pour le cadrage:", err); // Message de log mis à jour
      // Lancer une erreur qui sera interceptée par handleModalCreateInterview
      throw new Error(err.response?.data?.detail || err.message || i18n.t('errors.interview.createOrRefreshFailed'));
    } finally {
      setIsSubmitting(false);
    }
  }, [cadrageId, loadCadrageData, selectInterview]);

  // UPDATE (basé sur la logique de InterviewEditModal)
  const updateInterview = useCallback(async (interviewId: string, payload: InterviewUpdate) => {
    if (!interviewId) throw new Error(i18n.t('errors.interview.missingIdForUpdate'));
    if (!cadrageId) throw new Error(i18n.t('errors.interview.missingCadrageForUpdate'));

    setIsSubmitting(true);
    try {
      console.log(`useCadrageGuidedInterviews: Mise à jour de l'entretien ${interviewId}`, payload);
      // L'endpoint API est générique pour la mise à jour
      await interviewService.updateInterview(interviewId, payload);
      await loadCadrageData(cadrageId); // Rafraîchir
      // La modale se fermera probablement dans le composant appelant après succès
    } catch (err: any) {
      console.error(`Erreur lors de la mise à jour de l'entretien ${interviewId}:`, err);
      throw new Error(err.response?.data?.detail || err.message || i18n.t('errors.interview.updateFailed'));
    } finally {
      setIsSubmitting(false);
    }
  }, [cadrageId, loadCadrageData]);

  // DELETE (basé sur la logique de AuditGuidedInterviewsTab)
  const deleteInterview = useCallback(async (interviewId: string) => {
    if (!interviewId) throw new Error(i18n.t('errors.interview.missingIdForDelete'));
    if (!cadrageId) throw new Error(i18n.t('errors.interview.missingCadrageForDelete'));

    // Confirmation utilisateur (peut être gérée dans le composant UI)
    // if (!window.confirm(...)) return;

    setIsSubmitting(true);
    try {
      console.log(`useCadrageGuidedInterviews: Suppression de l'entretien ${interviewId}`);
      // L'endpoint API est générique pour la suppression
      await interviewService.deleteInterview(interviewId);
      await loadCadrageData(cadrageId); // Rafraîchir
      // Si l'entretien supprimé était celui en cours d'édition, nettoyer l'état local
      if (interviewToEdit?.id === interviewId) {
        setInterviewToEdit(null);
        setIsEditModalOpen(false); // Fermer la modale si elle était ouverte pour cet entretien
      }
      // Si l'entretien supprimé était le sélectionné, désélectionner
      if (selectedInterviewId === interviewId) {
        selectInterview(null);
      }
    } catch (err: any) {
      console.error(`Erreur lors de la suppression de l'entretien ${interviewId}:`, err);
      throw new Error(err.response?.data?.detail || err.message || i18n.t('errors.interview.deleteFailed'));
    } finally {
      setIsSubmitting(false);
    }
  }, [cadrageId, loadCadrageData, interviewToEdit, selectedInterviewId, selectInterview]);

  // --- Edit Modal Management ---
  const selectInterviewToEdit = useCallback((interview: Interview | null) => {
    setInterviewToEdit(interview);
  }, []);

  const openEditModal = useCallback(() => {
    if (interviewToEdit) { // Ouvrir seulement si un entretien est sélectionné pour édition
      setIsEditModalOpen(true);
    } else {
      console.warn("Tentative d'ouvrir la modale d'édition sans entretien sélectionné.");
    }
  }, [interviewToEdit]);

  const closeEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    // Optionnel: désélectionner l'entretien à l'édition lors de la fermeture ?
    // setInterviewToEdit(null); // Garder sélectionné peut être utile si l'utilisateur annule juste
  }, []);

  // --- Return Value ---
  return {
    interviews: storeInterviews || [], // Assurer que c'est toujours un tableau
    isLoading: isLoading || isSubmitting, // Combiner chargement global et soumission CRUD
    error, // Erreur globale du store
    selectedInterviewId,
    selectInterview,
    selectedInterviewData,
    updateInterview,
    createGuidedInterview,
    interviewToEdit,
    selectInterviewToEdit,
    isEditModalOpen,
    openEditModal,
    closeEditModal,
    deleteInterview,
  };
}
